/**
 * Archivo: MessageList.js
 * Fecha: 2026-05-20
 * Descripción: Componente que renderiza la lista de mensajes
 * Modificación:
 * - Corregido observer para que solo cargue una vez
 * - Agregado mensaje "No hay más mensajes"
 * - Keys únicas para evitar duplicados
 */

import React, { useEffect, useRef } from 'react';
import { FiCheckCircle, FiPackage, FiEdit3, FiCornerUpLeft } from 'react-icons/fi';
import { getImagenUrl } from 'shared/api';
import {
  FasePedidoCard,
  CambiosProductoCard,
  ImagenMessageCard,
  AudioMessageCard,
  DocumentoMessageCard,
} from './ChatCards';
import { styles } from './styles';

function isDark() {
  try {
    return document.documentElement.getAttribute('data-dark') === 'true';
  } catch {
    return false;
  }
}

const ProductCard = ({ prod, showAdd = true }) => {
  const esPesable = prod.pesable || false;
  const stockLabel =
    prod.stock > 20 ? '✅ Disponible' : prod.stock > 0 ? '⚠️ Pocas und' : '❌ Agotado';
  const stockColor = prod.stock > 20 ? '#34a853' : prod.stock > 0 ? '#fbbc04' : '#ea4335';
  const imgSrc = getImagenUrl(prod.imagen_url);
  const [imgError, setImgError] = React.useState(false);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'var(--agx-skeleton)',
        borderRadius: '10px',
        padding: '8px 12px',
        gap: '8px',
        border: '1px solid var(--agx-border)',
        fontSize: '13px',
      }}
    >
      {imgSrc && !imgError ? (
        <img
          src={imgSrc}
          alt={prod.nombre}
          onError={() => setImgError(true)}
          style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
        />
      ) : (
        <FiPackage size={16} style={{ color: '#1a73e8', flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: 'var(--agx-text)', fontSize: '13px' }}>
          {prod.nombre}
          {esPesable && (
            <span style={{ fontSize: '10px', color: 'var(--agx-muted)', marginLeft: '4px' }}>
              ⚖️ pesable
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '2px' }}>
          <span style={{ color: 'var(--agx-pri2)', fontWeight: 500 }}>${prod.precio}</span>
          <span style={{ color: 'var(--agx-muted)' }}>Bs {prod.precio_bs}</span>
          <span style={{ color: stockColor, fontSize: '11px' }}>{stockLabel}</span>
        </div>
      </div>
      {prod.stock > 0 && !esPesable && (
        <span
          style={{
            backgroundColor: 'var(--agx-blue-bg)',
            color: 'var(--agx-pri2)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {prod.stock} und
        </span>
      )}
      {esPesable && (
        <span
          style={{
            backgroundColor: 'var(--agx-amber-bg)',
            color: 'var(--agx-amber)',
            padding: '2px 8px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          {(prod.stock / 1000).toFixed(1)}kg
        </span>
      )}
    </div>
  );
};

const ReplyPreview = ({ respuesta }) => {
  if (!respuesta) return null;
  const autorLabel =
    respuesta.autor === 'cliente'
      ? 'Cliente'
      : respuesta.autor === 'asesor' || respuesta.autor === 'agente'
        ? 'Asesor'
        : respuesta.autor === 'bot'
          ? 'Bot'
          : 'Sistema';
  const esImagen = respuesta.tipo === 'imagen';
  const esAudio = respuesta.tipo === 'audio';
  const esDocumento = respuesta.tipo === 'documento';
  const texto = esImagen ? 'Imagen' : esAudio ? 'Audio' : esDocumento ? 'Documento' : respuesta.mensaje;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        borderLeft: '3px solid ' + (isDark() ? '#60a5fa' : '#1a73e8'),
        paddingLeft: '8px',
        marginBottom: '6px',
        fontSize: '12px',
        opacity: 0.9,
      }}
    >
      <span style={{ fontWeight: 600, fontSize: '11px' }}>
        {esImagen ? '📷 ' : esAudio ? '🎤 ' : esDocumento ? '📄 ' : ''}
        {autorLabel}
      </span>
      <span
        style={{
          color: 'var(--agx-muted)',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {texto}
      </span>
    </div>
  );
};

const replyBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--agx-faint)',
  padding: '4px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  flexShrink: 0,
};

const MessageList = ({
  mensajes,
  shouldScrollToBottom = false,
  onLoadMore,
  hasMore,
  loadingMore,
  onReply,
}) => {
  const messagesEndRef = useRef(null);
  const topSentinelRef = useRef(null);
  const observerRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const isLoadingRef = useRef(false);

  // Scroll al final al abrir el chat Y cuando llegan nuevos mensajes
  useEffect(() => {
    if (!shouldScrollToBottom || !messagesEndRef.current) return;
    if (!hasScrolledRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      hasScrolledRef.current = true;
    } else if (!isLoadingRef.current && mensajes.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [shouldScrollToBottom, mensajes.length]);

  // Resetear el flag cuando cambia el chat (nuevo cliente seleccionado)
  useEffect(() => {
    hasScrolledRef.current = false;
    isLoadingRef.current = false;
  }, [shouldScrollToBottom]);

  // IntersectionObserver para detectar cuando se llega al tope
  useEffect(() => {
    if (!topSentinelRef.current || !onLoadMore) return;

    // Desconectar observer anterior si existe
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Crear nuevo observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];

        // Condiciones para cargar:
        // 1. El centinela es visible
        // 2. Hay más mensajes (hasMore = true)
        // 3. No está cargando actualmente
        // 4. No estamos en medio de una carga
        if (firstEntry.isIntersecting && hasMore && !loadingMore && !isLoadingRef.current) {
          console.log('📜 Observer: detectó el tope - Cargando más mensajes antiguos...');
          isLoadingRef.current = true;
          onLoadMore();

          // Resetear el flag después de 1.5 segundos para permitir futuras cargas
          setTimeout(() => {
            isLoadingRef.current = false;
          }, 1500);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '10px 0px 0px 0px',
      }
    );

    observerRef.current.observe(topSentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [onLoadMore, hasMore, loadingMore]);

  // Cuando termina la carga, resetear el flag
  useEffect(() => {
    if (!loadingMore) {
      setTimeout(() => {
        isLoadingRef.current = false;
      }, 500);
    }
  }, [loadingMore]);

  const formatearHora = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    const ahora = new Date();
    const esHoy = date.toDateString() === ahora.toDateString();

    if (esHoy) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    }
  };

  const formatearFechaHeader = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    const ahora = new Date();
    const ayer = new Date(ahora);
    ayer.setDate(ayer.getDate() - 1);

    if (date.toDateString() === ahora.toDateString()) {
      return 'Hoy';
    } else if (date.toDateString() === ayer.toDateString()) {
      return 'Ayer';
    } else {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    }
  };

  const esMismoAutor = (a, b) => {
    if (!a || !b) return false;
    if (a.autor === 'sistema' || b.autor === 'sistema') return false;
    if (
      a.tipo === 'fase' || a.tipo === 'cambios' ||
      b.tipo === 'fase' || b.tipo === 'cambios'
    )
      return false;
    return a.autor === b.autor;
  };
  const dentroDe5Min = (a, b) => {
    if (!a?.hora || !b?.hora) return false;
    return Math.abs(new Date(a.hora) - new Date(b.hora)) < 5 * 60 * 1000;
  };

  if (mensajes.length === 0) {
    return (
      <div style={styles.emptyChatState}>
        <div style={styles.emptyChatIcon}>💬</div>
        <p>No hay mensajes aún</p>
        <p style={styles.emptyChatSub}>Envía un mensaje para comenzar la conversación</p>
      </div>
    );
  }

  return (
    <div style={styles.mensajesArea}>
      <style>{styles.msgAnim}</style>
      {/* Elemento centinela para detectar cuando se llega al tope - solo si hay más mensajes */}
      {hasMore && (
        <div
          ref={topSentinelRef}
          style={{ height: '1px', marginTop: '0px', visibility: 'visible' }}
        />
      )}

      {/* Indicador de carga */}
      {loadingMore && (
        <div style={{ textAlign: 'center', padding: '10px', color: 'var(--agx-muted)' }}>
          <span>Cargando mensajes antiguos...</span>
        </div>
      )}

      {/* Mensaje cuando no hay más mensajes para cargar */}
      {!hasMore && mensajes.length > 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '20px',
            color: 'var(--agx-faint)',
            fontSize: '12px',
          }}
        >
          ─── No hay más mensajes ───
        </div>
      )}

      {mensajes.map((msg, idx) => {
        // Usar key única combinando id, cliente y mensaje para evitar duplicados
        const uniqueKey = msg.id
          ? `${msg.idcliente}_${msg.id}_${idx}`
          : `${msg.idcliente}_${msg.mensaje}_${msg.hora}_${idx}`;

        const fechaMsg = new Date(msg.hora);
        const fechaAnterior = idx > 0 ? new Date(mensajes[idx - 1].hora) : null;
        const mostrarFecha =
          !fechaAnterior || fechaMsg.toDateString() !== fechaAnterior.toDateString();

        const esDelAsesor = msg.autor === 'agente' || msg.autor === 'bot' || msg.autor === 'asesor';
        const esSistema = msg.autor === 'sistema' || msg.tipo === 'modificacion';
        const alineacion = esSistema ? 'center' : esDelAsesor ? 'flex-end' : 'flex-start';
        const colorBurbuja = esSistema
          ? isDark()
            ? '#78350f'
            : '#fef7e0'
          : esDelAsesor
            ? '#1a73e8'
            : isDark()
              ? '#1e293b'
              : '#ffffff';
        const colorTexto = esSistema
          ? isDark()
            ? '#fcd34d'
            : '#5f6368'
          : esDelAsesor
            ? '#ffffff'
            : isDark()
              ? '#e2e8f0'
              : '#202124';
        const borde = esSistema
          ? '1px solid ' + (isDark() ? '#fbbf24' : '#fdd835')
          : esDelAsesor
            ? 'none'
            : '1px solid ' + (isDark() ? '#334155' : '#e0e0e0');

        const meta = msg.metadata;
        const esTipoProducto =
          msg.tipo === 'productos' || msg.tipo === 'agregado' || msg.tipo === 'pedido';
        const tieneProductos = meta && (meta.productos || meta.producto || meta.items_agregados);
        const mostrarProductCards = esTipoProducto && tieneProductos;

        const esFase = msg.tipo === 'fase';
        const esCambios = msg.tipo === 'cambios' || (esSistema && meta && meta.cambios);
        const esImagen = msg.tipo === 'imagen';
        const esAudio = msg.tipo === 'audio';
        const esDocumento = msg.tipo === 'documento';
        const esTarjetaEspecial = esFase || esCambios;
        const esReplyable =
          ['cliente', 'asesor', 'agente', 'bot'].includes(msg.autor) &&
          ['texto', 'imagen', 'audio', 'documento'].includes(msg.tipo);

        if (esTarjetaEspecial) {
          return (
            <React.Fragment key={uniqueKey}>
              {mostrarFecha && (
                <div style={styles.dateSeparator}>
                  <span style={styles.dateChip}>{formatearFechaHeader(msg.hora)}</span>
                </div>
              )}
              <div style={{ ...styles.mensaje, justifyContent: 'center' }}>
                <div style={{ maxWidth: '90%', width: '100%' }}>
                  {esFase && (
                    <FasePedidoCard fase={meta?.fase || msg.mensaje} pedidoId={msg.pedido_id} />
                  )}
                  {esCambios && (
                    <CambiosProductoCard cambios={meta?.cambios || []} mensaje={msg.mensaje} />
                  )}
                  <div style={{ textAlign: 'center', marginTop: '4px' }}>
                    <span style={styles.mensajeHora}>{formatearHora(msg.hora)}</span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        }

        const prevMsg = mensajes[idx - 1];
        const nextMsg = mensajes[idx + 1];
        const esContinuacion = esMismoAutor(msg, prevMsg) && dentroDe5Min(msg, prevMsg);
        const esContinuado = esMismoAutor(msg, nextMsg) && dentroDe5Min(msg, nextMsg);
        const esUltimoDeGrupo = !esContinuado;

        const borderRadius = esContinuado
          ? '14px'
          : alineacion === 'flex-end'
            ? '14px 14px 4px 14px'
            : '14px 14px 14px 4px';
        const mostrarCola = esUltimoDeGrupo && !esSistema;
        const marginTop = esContinuacion ? 2 : 8;

        return (
          <React.Fragment key={uniqueKey}>
            {mostrarFecha && (
              <div style={styles.dateSeparator}>
                <span style={styles.dateChip}>{formatearFechaHeader(msg.hora)}</span>
              </div>
            )}
            <div style={{ ...styles.mensaje, justifyContent: alineacion, alignItems: 'center', gap: '4px', marginTop }}>
              {esReplyable && onReply && alineacion === 'flex-end' && (
                <button
                  type="button"
                  onClick={() => onReply(msg)}
                  style={replyBtnStyle}
                  aria-label="Responder mensaje"
                >
                  <FiCornerUpLeft size={15} />
                </button>
              )}
              <div
                style={{
                  ...styles.mensajeBubble,
                  backgroundColor: colorBurbuja,
                  color: colorTexto,
                  border: borde,
                  borderRadius,
                  maxWidth: mostrarProductCards ? '90%' : '65%',
                }}
              >
                {mostrarCola && (
                  <span
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      ...(alineacion === 'flex-end' ? { right: -4 } : { left: -4 }),
                      width: 9,
                      height: 9,
                      backgroundColor: colorBurbuja,
                      transform: 'rotate(45deg)',
                    }}
                  />
                )}
                <ReplyPreview respuesta={msg.respuesta_a} />
                {esSistema && (
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: 'var(--agx-warning-text)',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <FiEdit3 size={12} /> Sistema
                  </div>
                )}
                {msg.autor === 'bot' && <div style={styles.botLabel}>🤖 Bot</div>}
                {esImagen ? (
                  <ImagenMessageCard url={meta?.url || ''} />
                ) : esAudio ? (
                  <AudioMessageCard
                    url={meta?.url || ''}
                    transcripcion={meta?.transcripcion || ''}
                    duracion={meta?.duracion || 0}
                  />
                ) : esDocumento ? (
                  <DocumentoMessageCard
                    url={meta?.url || ''}
                    filename={meta?.filename || ''}
                    nombre={meta?.nombre_original || msg.mensaje}
                  />
                ) : (
                  <div style={styles.mensajeTexto}>{msg.mensaje}</div>
                )}
                {msg.pedido_id && (
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: 'var(--agx-pri2)',
                      marginTop: '3px',
                      opacity: 0.75,
                    }}
                  >
                    Pedido #{msg.pedido_id}
                  </div>
                )}
                {esUltimoDeGrupo && (
                  <div style={styles.mensajeFooter}>
                    <span style={styles.mensajeHora}>{formatearHora(msg.hora)}</span>
                    {msg.autor === 'asesor' && (
                      <span style={styles.mensajeCheck}>
                        <FiCheckCircle size={12} />
                      </span>
                    )}
                    {msg.autor === 'cliente' && !msg.leido && (
                      <span style={styles.mensajeNoLeido}>📖 No leído</span>
                    )}
                  </div>
                )}

                {/* Product cards visuales para el asesor */}
                {mostrarProductCards && (
                  <div
                    style={{
                      marginTop: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                    }}
                  >
                    {meta.producto && <ProductCard prod={meta.producto} />}
                    {meta.items_agregados &&
                      meta.items_agregados.map((p, i) => <ProductCard key={i} prod={p} />)}
                    {meta.productos &&
                      meta.productos.map((p, i) => (
                        <ProductCard key={i} prod={p} showAdd={false} />
                      ))}
                  </div>
                )}
              </div>
              {esReplyable && onReply && alineacion !== 'flex-end' && (
                <button
                  type="button"
                  onClick={() => onReply(msg)}
                  style={replyBtnStyle}
                  aria-label="Responder mensaje"
                >
                  <FiCornerUpLeft size={15} />
                </button>
              )}
            </div>
          </React.Fragment>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
